import { createFeatureSelector, createSelector } from '@ngrx/store';
import { Map } from 'immutable';

import { selectCitationStyles } from '®app/ngrx/state';
import { OnlineResource } from '®shared/bibliographic-resource/online-resource';
import {
  ContainerData,
  selectContainerData,
} from '®shared/container-structure/state';
import {
  BasicCitationStyle,
  DEFAULT_CITATION_STYLES,
} from '®shared/reading-list/citation-switcher';
import { Citation, EditableItem } from '®shared/reading-list/models';
import { QuickFilter } from '®shared/reading-list/quick-filters';
import { ResourceTypeFilter } from '®shared/reading-list/resource-type-filter';
import { CitationStyle } from '®shared/tenant';
import {
  ReadingIntentionsByResourceId,
  StudyNotesByResourceId,
  selectReadingIntentionsByResourceForUser,
  selectStudyNotesByResourceForUser,
} from '®shared/user-annotations';

import {
  filterEntryIds,
  getItemCountByQuickFilters,
  getItemCountForResourceTypeFilter,
} from './list-items-utils';

export const STATE_LIST_ITEMS = 'listItems';

export type ListItemsById = Map<string, EditableItem>;

export interface ListItemsState {
  listItemsById: ListItemsById;
  onlineResourcesByItemId: Map<string, OnlineResource>;
  citationsByStyle: Map<string, Map<string, Citation>>;
  currentCitationStyleId?: string;
  expandedItemId: string | null;
  textFilter?: string;
  quickFilter?: QuickFilter;
  resourceTypeFilter?: ResourceTypeFilter;
  itemsWithNoImportanceCount?: number;
}

export const LIST_ITEMS_INITIAL_STATE = {
  listItemsById: Map<string, EditableItem>(),
  onlineResourcesByItemId: Map<string, OnlineResource>(),
  citationsByStyle: Map<string, Map<string, Citation>>(),
  expandedItemId: null,
};

const selectListItemsState =
  createFeatureSelector<ListItemsState>(STATE_LIST_ITEMS);

export const selectListItemsById = createSelector(
  selectListItemsState,
  (state: ListItemsState) => state && state.listItemsById,
);

export const selectOnlineResourcesByItemId = createSelector(
  selectListItemsState,
  (state: ListItemsState) => state && state.onlineResourcesByItemId,
);

export const selectCitationsByStyle = createSelector(
  selectListItemsState,
  (state: ListItemsState) => state && state.citationsByStyle,
);

export const selectCurrentCitationStyleId = createSelector(
  selectListItemsState,
  (state: ListItemsState): string | undefined =>
    state && state.currentCitationStyleId,
);

export const selectHasCurrentCitationStyle = createSelector(
  selectCurrentCitationStyleId,
  (currentStyleId: string | undefined): boolean =>
    currentStyleId ? true : false,
);

export const selectCurrentCitationStyle = createSelector(
  selectCitationStyles,
  selectCurrentCitationStyleId,
  (
    styles: CitationStyle[] | null,
    currentStyleId: string | undefined,
  ): BasicCitationStyle => {
    return styles && currentStyleId
      ? styles.find(style => style.id === currentStyleId)
      : DEFAULT_CITATION_STYLES.NONE;
  },
);

export const selectCitationsByItemForCurrentStyle = createSelector(
  selectCitationsByStyle,
  selectCurrentCitationStyleId,
  (
    citationsByStyle: Map<string, Map<string, Citation>>,
    currentStyleId: string | undefined,
  ): Map<string, Citation> | undefined =>
    currentStyleId && citationsByStyle.get(currentStyleId),
);

export const selectCitationsByItemForCurrentStyleHasErrors = createSelector(
  selectCitationsByItemForCurrentStyle,
  (citationsByItem: Map<string, Citation>): boolean => {
    if (!citationsByItem) {
      return false;
    }

    return citationsByItem.some(value => (value.error ? true : false));
  },
);

export const selectCurrentCitationForItem = (itemId: string) => {
  return createSelector(
    selectCitationsByItemForCurrentStyle,
    (citationsByItem: Map<string, Citation>): Citation | undefined =>
      citationsByItem && citationsByItem.get(itemId),
  );
};

export const selectExpandedItemId = createSelector(
  selectListItemsState,
  (state: ListItemsState): string | null => state && state.expandedItemId,
);

export const selectTextFilter = createSelector(
  selectListItemsState,
  (state: ListItemsState): string | undefined => state && state.textFilter,
);

export const selectQuickFilter = createSelector(
  selectListItemsState,
  (state: ListItemsState): QuickFilter | undefined =>
    state && state.quickFilter,
);

export const selectResourceTypeFilter = createSelector(
  selectListItemsState,
  (state: ListItemsState): ResourceTypeFilter | undefined =>
    state && state.resourceTypeFilter,
);

export const selectHasFilter = createSelector(
  selectTextFilter,
  selectQuickFilter,
  selectResourceTypeFilter,
  (textFilter, quickFilter, resourceTypeFilter) =>
    textFilter || quickFilter || resourceTypeFilter ? true : false,
);

export const selectFilteredEntryIds = createSelector(
  selectContainerData,
  selectListItemsById,
  selectTextFilter,
  selectQuickFilter,
  selectResourceTypeFilter,
  (
    containerData: ContainerData,
    itemsById: ListItemsById,
    textFilter: string,
    quickFilter: QuickFilter,
    resourceTypeFilter: ResourceTypeFilter,
  ): Map<string, boolean> =>
    filterEntryIds(
      containerData,
      itemsById,
      textFilter,
      quickFilter,
      resourceTypeFilter,
    ),
);

export const selectItemCountByQuickFilters = (userId: string | null) =>
  createSelector(
    selectContainerData,
    selectListItemsById,
    selectOnlineResourcesByItemId,
    selectStudyNotesByResourceForUser(userId),
    selectReadingIntentionsByResourceForUser(userId),
    (
      containerData: ContainerData,
      itemsById: ListItemsById,
      onlineResourcesByItemId: Map<string, OnlineResource>,
      studyNotesByUserAndResource: StudyNotesByResourceId,
      readingIntentionsByResourceForUser: ReadingIntentionsByResourceId,
    ) =>
      getItemCountByQuickFilters(
        containerData,
        itemsById,
        onlineResourcesByItemId,
        studyNotesByUserAndResource || {},
        readingIntentionsByResourceForUser || {},
      ),
  );

export const selectItemCountByResourceType = createSelector(
  selectContainerData,
  selectListItemsById,
  (containerData: ContainerData, itemsById: ListItemsById) =>
    getItemCountForResourceTypeFilter(containerData, itemsById),
);

export const selectHasVisibleItems = createSelector(
  selectFilteredEntryIds,
  entryIds => entryIds.some(isVisible => isVisible),
);

export function getInitialListItemsState(): ListItemsState {
  return LIST_ITEMS_INITIAL_STATE;
}
